import React, {useState} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {
	Breadcrumbs,
	Button,
	Dialog,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	Stack, TextField,
	Typography
} from "@mui/material";
import {Link} from "react-router-dom";
import {GraphQlDataGrid} from "../../../components/common/grid/GraphQlDataGrid";
import {graphQLServices} from "../../../services/GraphQLServices";
import {CUSTOM_ATTRIBUTES_SETUP_OVERVIEW, EXISTS_CUSTOM_ATTRIBUTES_SETUP, PUT_CUSTOM_ATTRIBUTES_SETUP} from "../Queries";
import {formatDateTimeShort, GlobalState} from "../../../global/GlobalState";
import {textColumn, tooltipColumn} from "../../../components/common/grid/Columns";
import {GridColDef, GridPagination} from "@mui/x-data-grid";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {translateFunction} from "../../../localization/TranslationService";
import {productsEditorDefaultTargetStage, productsEditorTargetStage} from "../ProductModuleOverview";
import {CustomAttributeSetup} from "../models/CustomAttributeSetup";
import {cloneMutationData} from "../Utils";
import {linkCustomAttributeSetupDetailsFor} from "./CustomAttributeSetupDetails";
import {HeaderSlot, StageSwitch} from "../../../components/onsite/Footer";


const translate = translateFunction("SureBasePortalProductsCatalog");

export const CustomAttributeSetupOverview = pageAuthorizedByGroup(function () {
	const stage = GlobalState.targetKiziStage(productsEditorDefaultTargetStage);
	const columns: GridColDef[] = [
		{
			field: "",
			headerName: translate("Open"),
			renderCell: (params) => (
				<Link to={linkCustomAttributeSetupDetailsFor(params.row.id)} style={{
					textDecoration: "initial"
				}}>
					<Button size="small" tabIndex={-1}>{translate("Open")}</Button>
				</Link>
			),
			sortable: false,
			editable: false,
			disableColumnMenu: stage !== productsEditorTargetStage,
			filterable: false,
			hideable: false,
		},
		{
			field: "description",
			headerName: translate("Description"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "mapping",
			headerName: translate("Mapping"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "creationDateTime",
			headerName: translate("Creation Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		},
		{
			field: "lastModifiedBy",
			headerName: translate("Last Modified By"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedDateTime",
			headerName: translate("Last Modified Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1
		}
		/* todo access */
	];
	
	const [resetKey, setResetKey] = useState(0);
	const updateGrid = () => setResetKey(resetKey + 1);
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/products">{translate("Products")}</Link>
			<Typography color="text.primary">{translate("Custom attributes setup")}</Typography>
		</Breadcrumbs>}
		center={<Paper style={{
			height: "100%",
		}}>
			<HeaderSlot><StageSwitch defaultTargetStage={productsEditorDefaultTargetStage}/></HeaderSlot>
			<GraphQlDataGrid
				rows={[]}
				query={CUSTOM_ATTRIBUTES_SETUP_OVERVIEW}
				graphFunction={"customAttributeSetup"}
				client={graphQLServices.productsClient}
				rowsPerPageOptions={[50, 500, 1000]}
				initialPageSize={50}
				columns={columns}
				getRowId={row => row.id}
				defaultSortModel={[
					{
						field: "description",
						sort: "asc"
					}
				]}
				queryVariables={{stage, resetKey}}
				initialState={{
					columns: {
						columnVisibilityModel: {
							creationDateTime: false
						},
					},
				}}
				slots={{
					footer: CustomFooterFactory(stage, updateGrid)
				}}
			/>
		</Paper>}
	/>;
}, ["Admin", "Product"]);

async function exists(item: CustomAttributeSetup): Promise<boolean> {
	const existsItems = await graphQLServices.productsClient.query(
		{
			query: EXISTS_CUSTOM_ATTRIBUTES_SETUP,
			variables: {
				stage: productsEditorTargetStage,
				description: item.description,
				mapping: item.mapping
			},
			fetchPolicy: "no-cache"
		}
	);
	
	const nodes: any[] = existsItems.data.customAttributeSetup.nodes;
	for (const item1 of nodes) {
		if (item1.id === item.id) {
			continue;
		}
		return true;
	}
	
	return false;
}


async function saveChanges(customAttributeSetup: CustomAttributeSetup) {
	await graphQLServices.productsClient.mutate({
		mutation: PUT_CUSTOM_ATTRIBUTES_SETUP,
		variables: {
			stage: productsEditorTargetStage,
			customAttributeSetup: cloneMutationData(customAttributeSetup),
		}
	});
}

function CustomFooterFactory(stage: string, updateGrid: () => void) {
	return function CustomFooter(props: any) {
		const [open, setOpen] = useState(false);
		
		const [description, setDescription] = useState("");
		const [mapping, setMapping] = useState("");
		
		const handleClickOpen = () => setOpen(true);
		const handleClose = () => setOpen(false);
		
		const handleCreate = async () => {
			const id = crypto.randomUUID();
			const newItem = {id, description, mapping} as CustomAttributeSetup;
			
			if (!newItem.description || newItem.description === "")
				throw new Error(translate(`Description field needs a value`));
			
			if (!newItem.mapping || newItem.mapping === "")
				throw new Error(translate(`Mapping field needs a value`));
			
			await exists(newItem).then(async (x) => {
				if (x) {
					throw new Error(translate(`Item already exists with the same description or mapping value`));
				}
				
				await saveChanges(newItem);
				setOpen(false);
				updateGrid();
			});
		};
		
		return <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} style={{
			padding: "0 10px"
		}}>
			<Stack direction={"row"} spacing={2} alignItems={"center"}>
				<Button
					disabled={stage !== productsEditorTargetStage}
					variant={"contained"}
					onClick={handleClickOpen}
				>{translate("Add")}</Button>
			</Stack>
			<Dialog open={open} onClose={handleClose}>
				<DialogTitle>{translate("New Custom attribute")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{translate("Please provide the following information.")}
					</DialogContentText>
					<form>
						<TextField
							margin="dense"
							label={translate("Description")}
							fullWidth
							variant="standard"
							required
							value={description}
							onChange={event => {
								setDescription(event.target.value);
							}}
						/>
						<TextField
							margin="dense"
							label={translate("Mapping")}
							fullWidth
							variant="standard"
							required
							value={mapping}
							onChange={event => {
								setMapping(event.target.value);
							}}
						/>
						<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
							<Button onClick={handleClose}>{translate("Cancel")}</Button>
							<div style={{flexGrow: 0, padding: "10px"}}>
								<ProcessButton
									defaultText={translate("Create")}
									inProgressText={translate("Creating")}
									completedText={translate("Created")}
									resetKeys={[description]}
									variant={"outlined"}
									onClick={handleCreate}/>
							</div>
						</Stack>
					</form>
				</DialogContent>
			</Dialog>
			<GridPagination {...props} labelRowsPerPage={translate("Rows per page:")}/>
		</Stack>;
	};
}
